import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import { useI18n, routes } from "@/i18n";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/components/AnimatedSection";
import { blogPosts } from "@/data/blogPosts";
import { m } from "framer-motion";
import heroHome from "@/assets/hero-home.webp";
import heroServices from "@/assets/hero-services.webp";
import GlobalPresenceGrid from "@/components/GlobalPresenceGrid";

const Home = () => {
  const { locale, t } = useI18n();
  const r = routes[locale];
  const recentPosts = [...blogPosts].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);

  return (
    <>
      {/* Hero — cinematic full-viewport */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroHome} alt="" loading="eager" decoding="async" fetchPriority="high" className="w-full h-full object-cover" style={{ objectPosition: '30% center' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-[hsl(226,55%,24%)]/50 to-black/40" />
        </div>
        <div className="container relative z-10 pt-20">
          <div className="max-w-3xl">
            <m.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <div className="w-16 h-px bg-accent mb-8" />
              <h1 className="font-heading text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.1] mb-6 text-white">
                {t.hero.title}
              </h1>
            </m.div>
            <m.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="text-lg md:text-xl text-white/70 mb-10 max-w-2xl leading-relaxed"
            >
              {t.hero.subtitle}
            </m.p>
            <m.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              className="flex flex-wrap gap-4 mb-12"
            >
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 px-8">
                <Link to={`/${locale}${r.contact}`}>
                  {t.hero.cta1} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/30 text-white bg-transparent hover:bg-white/10 px-8">
                <Link to={`/${locale}${r.approach}`}>{t.hero.cta2}</Link>
              </Button>
            </m.div>
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-wrap gap-x-8 gap-y-3"
            >
              {t.hero.bullets.map((bullet) => (
                <span key={bullet} className="flex items-center gap-3 text-sm text-white/60">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                  {bullet}
                </span>
              ))}
            </m.div>
          </div>
        </div>
      </section>

      {/* Manifesto — editorial POV */}
      <AnimatedSection className="py-8 md:py-10">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-heading text-xl md:text-2xl lg:text-3xl leading-[1.25] mb-4">
              <span className="text-foreground">{t.manifesto.line1a}</span>
            </p>
            <div className="w-8 h-px bg-border mx-auto mb-4" />
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              {t.manifesto.line2}
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* What We Do — editorial 2-column */}
      <AnimatedSection className="py-24 md:py-32">
        <div className="container">
          <div className="grid gap-16 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <div className="w-12 h-px bg-accent mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl leading-tight mb-6">{t.whatWeDo.title}</h2>
              <p className="text-muted-foreground leading-relaxed">{t.whatWeDo.subtitle}</p>
            </div>
            <div className="lg:col-span-3">
              <div className="space-y-0">
                {t.whatWeDo.services.map((service, i) => (
                  <Link key={service.link} to={`/${locale}${r.services}/${service.link}`} className="group block">
                    <div className="border-t border-border py-8 flex items-start justify-between gap-6">
                      <div>
                        <span className="text-xs text-muted-foreground/60 font-medium tracking-wider uppercase mb-2 block">0{i + 1}</span>
                        <h3 className="font-heading text-xl md:text-2xl mb-2 group-hover:text-accent transition-colors">{service.title}</h3>
                        <p className="text-sm text-muted-foreground max-w-md whitespace-pre-line">{service.desc}</p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-accent group-hover:translate-x-1 transition-all mt-8 shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Outcomes — dark section, large typography */}
      <AnimatedSection className="py-24 md:py-32 bg-primary text-primary-foreground">
        <div className="container">
          <div className="text-center mb-16">
            <div className="w-12 h-px bg-accent mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl">{t.outcomes.title}</h2>
          </div>
          <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4 items-stretch">
            {t.outcomes.items.map((item, i) => (
              <div key={item.number} className="flex flex-col text-center md:text-left h-full">
                <div className="font-heading text-xl md:text-2xl text-accent mb-3 min-h-[4rem] md:min-h-[4.5rem] flex items-start justify-center md:justify-start">
                  <span>{item.number}</span>
                </div>
                <div className="w-8 h-px bg-primary-foreground/20 mb-3 mx-auto md:mx-0" />
                <p className="text-sm text-primary-foreground/60 leading-relaxed">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Success Stories — editorial proof block */}
      <AnimatedSection className="pt-24 md:pt-32 pb-4 md:pb-6">
        <div className="container">
          <div className="max-w-2xl mb-16">
            <div className="w-12 h-px bg-accent mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl mb-4">{t.caseStudies.title}</h2>
            <p className="text-muted-foreground">{t.caseStudies.subtitle}</p>
          </div>
          <div className="grid gap-10 md:grid-cols-3">
            {t.caseStudies.items.map((item) => (
              <article key={item.title} className="group border-t border-border hover:border-accent transition-colors pt-8 flex flex-col">
                <h3 className="font-heading text-xl md:text-2xl mb-4 leading-snug">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">{item.body}</p>
                <div className="w-8 h-px bg-accent mb-3" />
                <p className="text-xs text-accent font-medium tracking-wider uppercase">{item.result}</p>
              </article>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Our Approach — timeline of 5 phases */}
      <AnimatedSection className="pt-16 md:pt-20 pb-24 md:pb-32">
        <div className="container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <div className="w-12 h-px bg-accent mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl mb-4">{t.approach.title}</h2>
              <p className="text-muted-foreground">{t.approach.subtitle} {t.approach.relationship}</p>
            </div>
            <Link to={`/${locale}${r.approach}`} className="text-sm font-medium text-muted-foreground hover:text-foreground flex items-center gap-2 transition-colors shrink-0">
              {t.insights.viewAll} <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {/* One timeline, laid out vertically on mobile and horizontally on desktop. */}
          <div className="relative">
            <div className="absolute left-5 top-0 bottom-0 w-px bg-border md:left-0 md:right-0 md:top-5 md:bottom-auto md:h-px md:w-auto" />
            <div className="relative grid grid-cols-1 gap-10 md:grid-cols-6 md:gap-6">
              {t.approach.phases.map((phase, i) => (
                <div key={phase.title} className="relative pl-16 md:pl-0">
                  <div className="absolute left-0 top-0 flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-heading text-sm z-10 md:relative md:mb-6">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-heading text-lg mb-2 md:text-base md:mb-3 lg:text-lg leading-snug">{phase.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{phase.summary}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Global Presence — interactive city images */}
      <AnimatedSection className="py-24 md:py-32 bg-muted">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="w-12 h-px bg-accent mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl mb-4">{t.globalPresence.title}</h2>
            {t.globalPresence.subtitle && <p className="text-muted-foreground">{t.globalPresence.subtitle}</p>}
          </div>
          <GlobalPresenceGrid offices={t.globalPresence.offices} />
        </div>
      </AnimatedSection>

      {/* Insights Preview */}
      <AnimatedSection className="py-24 md:py-32">
        <div className="container">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="w-12 h-px bg-accent mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl">{t.insights.title}</h2>
            </div>
            <Link to={`/${locale}${r.insights}`} className="text-sm font-medium text-muted-foreground hover:text-foreground flex items-center gap-2 transition-colors">
              {t.insights.viewAll} <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {recentPosts.map((post, i) => {
              const content = post[locale as keyof typeof post] as { title: string; excerpt: string };
              return (
                <Link key={post.id} to={`/${locale}${r.insights}/${post.slug}`} className="group block">
                  <div className={`${i === 0 ? "md:row-span-2" : ""}`}>
                    <div className="border-t-2 border-border group-hover:border-accent transition-colors pt-6">
                      <span className="text-xs text-muted-foreground/60 font-medium tracking-wider uppercase">{post.date}</span>
                      <h3 className="font-heading text-lg md:text-xl mt-3 mb-3 group-hover:text-accent transition-colors">{content.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-3">{content.excerpt}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </AnimatedSection>

      {/* CTA — cinematic image band */}
      <section className="relative py-32 md:py-40">
        <div className="absolute inset-0">
          <img src={heroServices} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ objectPosition: '60% center' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(226,55%,24%)]/90 to-[hsl(226,55%,24%)]/70" />
        </div>
        <div className="container relative z-10 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="w-12 h-px bg-accent mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">{t.cta.title}</h2>
            
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 px-10">
              <Link to={`/${locale}${r.contact}`}>
                {t.cta.button} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
